import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import TruncateMarkup from 'react-truncate-markup';
import { Product, ProductVariant } from '@picsio/icons';

import Tag from '../../Tag';
import Tooltip from '../../Tooltip';
import s from './styles.module.scss';

const MAX_VISIBLE_OWNERS = 3;
const OVERFLOW_ATOM_KEY = '__overflow__';

const bulkOwnerPropType = PropTypes.shape({
  key: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['master', 'masterVariant']).isRequired,
  title: PropTypes.string.isRequired,
});

const renderOwnerChip = (owner, { wrapTooltip = false } = {}) => {
  const chip = (
    <Tag
      key={owner.key}
      className={s.chip}
      type="product"
      text={owner.title}
      icon={owner.type === 'masterVariant' ? <ProductVariant /> : <Product />}
      iconColor="inherit"
      hideCloseBtn
      tooltipText={wrapTooltip ? null : owner.title}
    />
  );

  if (!wrapTooltip) return chip;

  return (
    <Tooltip content={owner.title} placement="top">
      {chip}
    </Tooltip>
  );
};

const OwnerOverflowTrigger = ({ owners }) => (
  <Tooltip
    content={(
      <div className={s.tooltipColumn}>
        {owners.map((owner) => renderOwnerChip(owner))}
      </div>
    )}
    interactive
    placement="top"
  >
    <span className={s.overflow}>...</span>
  </Tooltip>
);

OwnerOverflowTrigger.propTypes = {
  owners: PropTypes.arrayOf(bulkOwnerPropType).isRequired,
};

const BulkProductOwnerChips = ({ owners }) => {
  const cappedOwners = useMemo(
    () => owners.slice(0, MAX_VISIBLE_OWNERS),
    [owners],
  );
  const alwaysHidden = useMemo(
    () => owners.slice(MAX_VISIBLE_OWNERS),
    [owners],
  );

  const renderEllipsis = useCallback((node) => {
    const renderedChildren = React.Children.toArray(node.props.children);
    const renderedOwnerCount = renderedChildren.filter(
      (child) => child.key !== OVERFLOW_ATOM_KEY,
    ).length;
    const hiddenByWidth = cappedOwners.slice(renderedOwnerCount);

    if (!hiddenByWidth.length) return null;

    return <OwnerOverflowTrigger owners={[...hiddenByWidth, ...alwaysHidden]} />;
  }, [alwaysHidden, cappedOwners]);

  if (!owners.length) return null;

  return (
    <div className={s.wrapper}>
      <TruncateMarkup lines={1} lineHeight="24px" ellipsis={renderEllipsis}>
        <div className={s.row}>
          {cappedOwners.map((owner) => (
            <TruncateMarkup.Atom key={owner.key}>
              {renderOwnerChip(owner, { wrapTooltip: true })}
            </TruncateMarkup.Atom>
          ))}
          <If condition={alwaysHidden.length}>
            <TruncateMarkup.Atom key={OVERFLOW_ATOM_KEY}>
              <OwnerOverflowTrigger owners={alwaysHidden} />
            </TruncateMarkup.Atom>
          </If>
        </div>
      </TruncateMarkup>
    </div>
  );
};

BulkProductOwnerChips.propTypes = {
  owners: PropTypes.arrayOf(bulkOwnerPropType),
};

BulkProductOwnerChips.defaultProps = {
  owners: [],
};

export default BulkProductOwnerChips;
